import pica from "pica";
import { db } from "../storage/database";
import { deleteAssetFiles, writeAssetFile } from "../storage/asset-store";
import { makeId, type AssetRecord, type PhotoItem } from "../domain/project";

const imageResizer = pica({ features: ["js", "wasm", "ww"] });
const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

interface PhotoMetadata {
  DateTimeOriginal?: Date | string;
  latitude?: number;
  longitude?: number;
}

async function readMetadata(file: File): Promise<PhotoMetadata> {
  let buffer: ArrayBuffer;
  try {
    buffer = await file.arrayBuffer();
  } catch {
    return {};
  }
  return new Promise((resolve) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL("./metadata.worker.ts", import.meta.url), { type: "module" });
    } catch {
      resolve({});
      return;
    }
    let settled = false;
    const finish = (value: PhotoMetadata) => {
      if (settled) return;
      settled = true;
      worker.terminate();
      resolve(value);
    };
    worker.onmessage = (event) => finish(event.data?.ok ? event.data.metadata || {} : {});
    worker.onerror = () => finish({});
    try {
      worker.postMessage(buffer);
    } catch {
      finish({});
    }
  });
}

async function resizeImage(source: ImageBitmap, maxSide: number, quality: number) {
  const ratio = Math.min(1, maxSide / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * ratio));
  const height = Math.max(1, Math.round(source.height * ratio));
  const from = document.createElement("canvas");
  from.width = source.width;
  from.height = source.height;
  from.getContext("2d", { alpha: false })!.drawImage(source, 0, 0);
  const to = document.createElement("canvas");
  to.width = width;
  to.height = height;
  await imageResizer.resize(from, to, { quality: 3 });
  return imageResizer.toBlob(to, "image/webp", quality);
}

export async function importPhoto(projectId: string, file: File, order: number) {
  if (!ACCEPTED_TYPES.has(file.type)) {
    throw new Error(`${file.name} 暂不支持，请先转换为 JPEG、PNG 或 WebP`);
  }
  const [bitmap, metadata] = await Promise.all([
    createImageBitmap(file, { imageOrientation: "from-image" }),
    readMetadata(file),
  ]);
  const assetId = makeId("asset");
  const metadataDate = metadata.DateTimeOriginal ? new Date(metadata.DateTimeOriginal) : undefined;
  const capturedAt = metadataDate && !Number.isNaN(metadataDate.getTime())
    ? metadataDate.toISOString()
    : undefined;
  try {
    const [preview, thumbnail] = await Promise.all([
      resizeImage(bitmap, 2400, 0.86),
      resizeImage(bitmap, 480, 0.78),
    ]);
    const writes = await Promise.allSettled([
      writeAssetFile(projectId, assetId, "original", file),
      writeAssetFile(projectId, assetId, "preview", preview),
      writeAssetFile(projectId, assetId, "thumbnail", thumbnail),
    ]);
    const failedWrite = writes.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failedWrite) throw failedWrite.reason;
    const asset: AssetRecord = {
      id: assetId,
      projectId,
      originalName: file.name,
      mimeType: file.type,
      width: bitmap.width,
      height: bitmap.height,
      size: file.size,
      capturedAt,
      latitude: metadata.latitude,
      longitude: metadata.longitude,
      createdAt: new Date().toISOString(),
    };
    await db.assets.put(asset);
    const item: PhotoItem = {
      id: makeId("item"),
      assetId,
      title: file.name.replace(/\.[^.]+$/, ""),
      caption: "",
      date: capturedAt?.slice(0, 10),
      people: [],
      order,
    };
    return { asset, item };
  } catch (error) {
    try {
      await deleteAssetFiles(projectId, assetId);
      await db.assets.delete(assetId);
    } catch {
      // Preserve the original import error even if best-effort cleanup fails.
    }
    throw error;
  } finally {
    bitmap.close();
  }
}
